import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Button, Dropdown, Form, Input, MenuProps, Modal, Space, Tag, Avatar } from "antd";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import JutgeColors from "../../../styles/JutgeColors";
import { PostDuda } from "../../../dao/PostDuda";
import HomeColors from "../../../styles/HomeColors";
import { AuthContext } from "../../../config/AuthContext";
import remarkBreaks from "remark-breaks";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { PostAnswerRating } from "../../../dao/PostAnserRating";
import { useParams } from "react-router-dom";
import { DownOutlined, SendOutlined, UserOutlined, RobotOutlined, LikeOutlined, LikeFilled, DislikeOutlined, DislikeFilled, MehOutlined, MehFilled } from "@ant-design/icons";
import { DeletePreguntaId } from "../../../dao/DeletePreguntaId";
import { DeleteDuda } from "../../../dao/DeleteDuda";
import itemsIA from "../problemesId/content/SortirCuaArrayIA";
import UseWebSocketSubscription from "../../UseWebSocketSubscription";
import { GetInteraccionsId } from "../../../dao/GetInteraccionsId";
import { CodeOutlined } from "@ant-design/icons";
import ProblemaIdEstadistiques from "../problemesId/content/ProblemaIdEstadistiques";

interface InteractionModalProps {
    modalVisible: boolean;
    setModalVisible: (visible: boolean) => void;
    onClose?: () => void;
    selectedRecord?: any;
    practica?: boolean;
    tasca?: boolean;
    copypaste?: number;
    initialMessage?: string;
    inline?: boolean;
    hideVereCodi?: boolean;
    fromWelcome?: boolean;
    dudasEnabled?: boolean;
    isEnCua?: boolean;
    onProfRequest?: () => void;
    onExitQueue?: (estat: number) => void;
}

const ChatModal: React.FC<InteractionModalProps> = ({
    modalVisible,
    setModalVisible,
    selectedRecord,
    onClose,
    practica = false,
    tasca = false,
    copypaste = 0,
    initialMessage = "",
    inline = false,
    hideVereCodi = false,
    fromWelcome = false,
    dudasEnabled = false,
    isEnCua = false,
    onProfRequest,
    onExitQueue
}) => {
    const [sending, setSending] = useState(false);
    const [ratings, setRatings] = useState<{ [key: number]: string }>({});
    const [isDisabled, setIsDisabled] = useState(true);
    const { id_problema, id_activitat, id_projecte, id_tasca, id_aula, id_practica } = useParams();
    const { alumne } = useContext(AuthContext);
    const [record, setRecord] = useState(selectedRecord);
    const nomAlumne = alumne?.alumne?.nom || "usuari";
    const { userId, user, userPermisos } = useContext(AuthContext);

    // Lógica refinada para "veure codi"
    const isAdmin = user.tipus === "1" || user.tipus === "0";
    const hasJutgeAccess = userPermisos?.some(p => p.accio?.includes('jutge-online'));

    // Si hideVereCodi es true, SIEMPRE es false. Si no, aplicamos el resto de condiciones.
    const canSeeCode = hideVereCodi ? false : (
        !!record?.id_user &&
        !tasca &&
        ((isAdmin && hasJutgeAccess) || (record?.id_user !== userId))
    );

    // Stats Modal State
    const [statsVisible, setStatsVisible] = useState(false);

    const [form] = Form.useForm();
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (selectedRecord) {
            checkDades();
        }
    }, [selectedRecord]);

    useEffect(() => {
        if (initialMessage && record?.id === 0) {
            form.setFieldsValue({ message: initialMessage });
        }
    }, [initialMessage, record, form]);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [record]);

    // Use a ref to keep the latest callback logic without changing the function identity
    const latestOnMessage = useRef((topic: string, msg: any) => { });

    useEffect(() => {
        latestOnMessage.current = (topic: string, msg: any) => {
            console.log("Nuevo mensaje en el chat:", msg);
            if (msg?.accion === "REFRESH" || msg === true) {
                reloadSelectedRecord();
            }
        };
    }); // Update on every render

    const handleSocketMessage = useCallback(
        (topic: string, msg: any) => {
            latestOnMessage.current(topic, msg);
        },
        [] // Stable identity
    );

    UseWebSocketSubscription([`/topic/duda/${record?.id}`], handleSocketMessage);

    function reloadSelectedRecord() {
        checkDades(false);
    }

    async function checkDades(refresh: boolean = true) {
        if (!selectedRecord) return;
        // If we already have a record with a real ID (from handleSend), use that. 
        // Otherwise use selectedRecord.id.
        const idToFetch = record?.id && record.id !== 0 ? record.id : selectedRecord.id;

        try {
            const dades = await GetInteraccionsId(userId, idToFetch + "");
            if (dades) {
                // Preserve id_aula and id_practica from selectedRecord since GetInteraccionsId doesn't return them
                const preservedAula = selectedRecord?.id_aula;
                const preservedPractica = selectedRecord?.id_practica;
                setRecord({
                    ...dades,
                    id_aula: dades.id_aula ?? preservedAula,
                    id_practica: dades.id_practica ?? preservedPractica
                });
                console.log("Datos cargados:", dades, "| id_aula:", dades.id_aula ?? preservedAula, "| id_practica:", dades.id_practica ?? preservedPractica);
            } else {
                console.warn("No data received for ID:", idToFetch);
                // Only reset to 0 if we were trying to fetch 0 or if we really want to clear on error (usually we don't)
                // If we are in a valid chat (idToFetch != 0), keep existing state to avoid blink/wipe.
                if (idToFetch === 0 || idToFetch === "0") {
                    setRecord({ id: 0 });
                }
            }
        } catch (error) {
            console.error("Error checking dades:", error);
        }
    }

    function decodePrefixedBase64(str: string): string {
        try {
            const base64Data = str.includes(":") ? str.split(":").pop()! : str;
            const bytes = Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0));
            return new TextDecoder("utf-8").decode(bytes);
        } catch (e) {
            console.error("Error decoding base64 UTF-8:", e);
            return str;
        }
    }

    const preprocessLaTeX = (content: string) => {
        if (!content) return "";
        
        let processed = content
            .replace(/\\\(([\s\S]*?)\\\)/g, '$$$1$$')
            .replace(/\\\[([\s\S]*?)\\\]/g, '$$$$$1$$$$');
            
        processed = processed.replace(/<think>([\s\S]*?)<\/think>/gi, '<details className="ai-thought"><summary>💭 <i>Reflexió de la IA</i></summary>\n\n$1\n\n</details>');
        if (processed.includes("<think>") && !processed.includes("</think>")) {
           processed = processed.replace(/<think>([\s\S]*)/gi, '<details className="ai-thought" open><summary>💭 <i>Reflexió de la IA (Processant...)</i></summary>\n\n$1\n\n</details>');
        }
        
        return processed;
    };

    async function handleSend() {
        try {
            const values = await form.validateFields();
            const msg = values.message?.trim();
            if (!msg) return;

            setSending(true);

            let res;
            if (!tasca) {
                res = await PostDuda(
                    record.id_aula ?? "-1",
                    userId,
                    msg,
                    record.id === 0 ? id_problema : record.id_problema,
                    record.id,
                    practica ? (record.id === 0 ? record.id_practica : record.id_practica) : (record.id === 0 ? id_activitat : record.id_activitat)
                );
            } else {
                res = await PostDuda(
                    id_aula ?? "-1",
                    userId,
                    msg,
                    0,
                    record.id,
                    0,
                    id_projecte,
                    id_tasca
                );
            }

            form.resetFields();

            // Only close modal immediately if it's the first message (record.id === 0)
            if (record.id === 0) {
                setModalVisible(false);
                if (onClose) onClose();
            } else {
                // If it's not the first message, we don't close, we just wait for the websocket to refresh.
                // We set a flag to reload if needed, but websocket handles REFRESH.
            }

        } catch (err) {
            console.error("Error enviando mensaje:", err);
        } finally {
            setSending(false);
        }
    }

    async function rate(valor: string, ansId: number, id_aula: number) {
        setRatings((prev) => ({
            ...prev,
            [ansId]: prev[ansId] === valor ? "" : valor,
        }));

        try {
            await PostAnswerRating(id_aula, ansId, valor, userId);
        } catch (error) {
            console.error("Error al guardar valoración:", error);
        }
    }

    const handleMenuClick: MenuProps["onClick"] = async (e) => {
        const estat = -1 - parseInt(e.key);
        if (onExitQueue) {
            onExitQueue(estat);
        } else {
            await salirCola(estat);
        }
        setModalVisible(false);
        if (onClose) onClose();
    };

    async function salirCola(estat: number) {
        if (estat !== 0) setIsDisabled(true);
        await DeletePreguntaId(
            record?.id,
            userId,
            estat,
            id_projecte || "0",
            id_tasca || "0",
            id_activitat || "0"
        );
        if (record?.id_aula) {
            await DeleteDuda(record.id_aula + "", userId + "", estat);
        }
    }

    // Helper to check if a rating is selected (case-insensitive)
    const isSelected = (ans: any, type: string) => {
        const localRating = ratings[ans.id];
        const dbRating = ans.valoracio;

        // If we have a local change, prioritize it. 
        // If localRating is "" (toggled off), it means no selection.
        if (localRating !== undefined) {
            return localRating.toLowerCase() === type.toLowerCase();
        }

        // Fallback to DB value if it exists
        return dbRating && dbRating.toLowerCase() === type.toLowerCase();
    };

    const innerContent = (
        <div style={{ padding: 0, height: inline ? '100%' : '70vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f9f9f9', width: '100%', borderRadius: inline ? 8 : 0, overflow: 'hidden' }}>
            {record && (
                <>
                    {window.location.hostname === 'localhost' && (
                        <div style={{ background: '#fffbe6', borderBottom: '1px solid #ffe58f', padding: '2px 12px', fontSize: '0.75rem', color: '#888', fontFamily: 'monospace' }}>
                            🛠 DEBUG:
                            {' '}id_aula = <strong style={{ color: record?.id_aula != null && record.id_aula !== '-1' && record.id_aula !== -1 ? '#10a37f' : '#ff4d4f' }}>{String(record?.id_aula ?? 'undefined')}</strong>
                            {' | '}id_practica = <strong style={{ color: record?.id_practica != null && record.id_practica !== 0 ? '#10a37f' : '#ff4d4f' }}>{String(record?.id_practica ?? 'undefined')}</strong>
                        </div>
                    )}
                    {inline && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', backgroundColor: '#fff', borderBottom: '1px solid #f0f0f0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <Avatar size={40} src="/images/aiavatar1.png" icon={<RobotOutlined />} style={{ backgroundColor: '#10a37f' }} />
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontSize: '1.1em', fontWeight: 700, color: '#333' }}>Asistente Lanterns</span>
                                    <span style={{ fontSize: '0.8em', color: '#888' }}>{record?.id ? `ID: ${record.id}` : 'Nueva Conversación'}</span>
                                </div>
                            </div>
                            {canSeeCode && (
                                <Button icon={<CodeOutlined />} onClick={() => setStatsVisible(true)} type="default">
                                    Veure Codi Enviat
                                </Button>
                            )}
                            {fromWelcome && dudasEnabled && user.tipus === "2" && !isEnCua && (
                                <Button
                                    type="primary"
                                    danger
                                    icon={<UserOutlined />}
                                    onClick={async () => {
                                        setSending(true);
                                        await PostDuda(record?.id_aula || "-1", userId, "-1", 0, 0, 0);
                                        setSending(false);
                                        if (onProfRequest) onProfRequest();
                                        setModalVisible(false);
                                        if (onClose) onClose();
                                    }}
                                >
                                    Demanar ajuda al Professor/a
                                </Button>
                            )}
                        </div>
                    )}
                    {/* Chat Area */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px',
                        scrollBehavior: 'smooth'
                    }}>
                        {/* Initial Context / Greeting */}
                        {practica && !record.context && (
                            <div style={{ textAlign: 'center', margin: '20px 0', opacity: 0.6 }}>
                                <div style={{ fontSize: '3rem', marginBottom: 10 }}>👋</div>
                                <h3>Hola {nomAlumne}!</h3>
                                <p>En què et puc ajudar avui?</p>
                            </div>
                        )}

                        {(user.tipus === "1" || user.tipus === "0") && record?.context && (
                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                                <div style={{
                                    backgroundColor: '#fff',
                                    border: '1px solid #e5e5e5',
                                    borderRadius: '12px',
                                    padding: '16px',
                                    maxWidth: '90%',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                    position: 'relative'
                                }}>
                                    <div style={{
                                        position: 'absolute', top: -10, left: 16,
                                        backgroundColor: '#eee', padding: '2px 8px',
                                        borderRadius: 4, fontSize: '0.75rem', fontWeight: 'bold', color: '#666'
                                    }}>
                                        CONTEXTO
                                    </div>
                                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeRaw, rehypeKatex]}>
                                        {preprocessLaTeX(record.context)}
                                    </ReactMarkdown>
                                </div>
                            </div>
                        )}

                        {/* Initial Question */}
                        {record.question && (
                            <div style={{
                                display: "flex",
                                flexDirection: record.id_user + "" === userId + "" ? "row-reverse" : "row",
                                gap: 16,
                                alignItems: 'flex-start'
                            }}>
                                <Avatar
                                    src={record.avatar}
                                    icon={<UserOutlined />}
                                    size={38}
                                    style={{ flexShrink: 0, border: '2px solid white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                                />
                                <div style={{
                                    backgroundColor: record.id_user + "" === userId + "" ? '#0084ff' : '#ffffff',
                                    color: record.id_user + "" === userId + "" ? '#ffffff' : '#333333',
                                    padding: '12px 18px',
                                    borderRadius: '18px',
                                    borderBottomRightRadius: record.id_user + "" === userId + "" ? '4px' : '18px',
                                    borderBottomLeftRadius: record.id_user + "" !== userId + "" ? '4px' : '18px',
                                    maxWidth: '75%',
                                    boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                                    lineHeight: 1.5,
                                    fontSize: '0.95rem'
                                }}>
                                    <div style={{
                                        fontSize: '0.75rem',
                                        fontWeight: 'bold',
                                        marginBottom: 4,
                                        opacity: 0.8,
                                        textAlign: record.id_user + "" === userId + "" ? 'right' : 'left'
                                    }}>
                                        {(user.tipus === "1" || user.tipus === "0") ? `${record.nom} ${record.cognoms}` : "Anònim"}
                                    </div>
                                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeRaw, rehypeKatex]} components={{
                                        p: ({ node, ...props }) => <p style={{ margin: 0 }} {...props} />
                                    }}>
                                        {preprocessLaTeX(record.question)}
                                    </ReactMarkdown>
                                    <div style={{
                                        fontSize: "0.7em",
                                        opacity: 0.7,
                                        marginTop: 6,
                                        textAlign: 'right'
                                    }}>
                                        {new Date(record.moment).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Interactive Messages */}
                        {[...(record.answers || [])]
                            .sort((a, b) => new Date(a.moment).getTime() - new Date(b.moment).getTime())
                            .map((ans) => {
                                const isUser = ans.rol === "user";
                                const isMe = isUser && (ans.id_user + "" === userId + "");
                                const isAdmin = user.tipus === "1" || user.tipus === "0";

                                const liked = isSelected(ans, "Like");
                                const neutral = isSelected(ans, "Neutral");
                                const disliked = isSelected(ans, "Dislike");

                                let senderName = "Anònim";
                                let finalAvatar = "";
                                let FinalIcon = <UserOutlined />;
                                let avatarColor = '#ccc';

                                if (isAdmin) {
                                    if (ans.rol === "assistant") {
                                        senderName = "Lanterns AI";
                                        FinalIcon = <RobotOutlined />;
                                        avatarColor = '#10a37f';
                                        finalAvatar = "/images/aiavatar1.png";
                                    } else if (ans.rol === "teacher") {
                                        senderName = "Professor";
                                        FinalIcon = <RobotOutlined />;
                                        avatarColor = '#10a37f';
                                        finalAvatar = "/images/aiavatar1.png";
                                    } else {
                                        senderName = `${ans.nom || ''} ${ans.cognoms || ''}`;
                                        finalAvatar = ans.avatar;
                                    }
                                } else {
                                    if (isMe) {
                                        senderName = "Tu";
                                        finalAvatar = ans.avatar;
                                    } else {
                                        senderName = "Anònim";
                                        FinalIcon = <UserOutlined />;
                                        avatarColor = '#ccc';
                                        finalAvatar = "";
                                    }
                                }

                                return (
                                    <div
                                        key={ans.id}
                                        style={{
                                            display: "flex",
                                            flexDirection: isMe ? "row-reverse" : "row",
                                            gap: 16,
                                            alignItems: 'flex-start'
                                        }}
                                    >
                                        <Avatar
                                            src={finalAvatar || undefined}
                                            icon={FinalIcon}
                                            size={38}
                                            style={{
                                                flexShrink: 0,
                                                backgroundColor: avatarColor,
                                                border: '2px solid white',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                            }}
                                        />

                                        <div style={{
                                            backgroundColor: isMe ? '#0084ff' : '#ffffff',
                                            color: isMe ? '#ffffff' : '#333333',
                                            padding: '12px 18px',
                                            borderRadius: '18px',
                                            borderBottomRightRadius: isMe ? '4px' : '18px',
                                            borderBottomLeftRadius: !isMe ? '4px' : '18px',
                                            maxWidth: '75%',
                                            boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                                            lineHeight: 1.5,
                                            fontSize: '0.95rem'
                                        }}>
                                            <div style={{
                                                fontSize: '0.75rem',
                                                fontWeight: 'bold',
                                                marginBottom: 4,
                                                opacity: 0.8,
                                                textAlign: isMe ? 'right' : 'left'
                                            }}>
                                                {senderName}
                                            </div>
                                            <div className="markdown-content">
                                                <ReactMarkdown
                                                    remarkPlugins={[remarkGfm, remarkBreaks, remarkMath]}
                                                    rehypePlugins={[rehypeRaw, rehypeKatex]}
                                                >
                                                    {preprocessLaTeX(decodePrefixedBase64(ans.answer))}
                                                </ReactMarkdown>
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginTop: 8,
                                                paddingTop: 4,
                                                borderTop: isMe ? '1px solid rgba(255,255,255,0.2)' : '1px solid #f0f0f0'
                                            }}>
                                                <span style={{ fontSize: "0.7em", opacity: 0.7 }}>
                                                    {new Date(ans.moment).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </span>

                                                {(ans.id_user + "" !== userId + "" || ans.rol === "assistant") && (
                                                    <>
                                                        {(user.tipus === "1" || user.tipus === "0") ? (
                                                            <div style={{ display: "flex", gap: 12, opacity: 0.8, alignItems: 'center' }}>
                                                                <div style={{ display: "flex", alignItems: "center", gap: 4, color: '#52c41a', fontSize: '0.8rem' }} title="Likes">
                                                                    <LikeFilled style={{ fontSize: '14px' }} /> <span style={{ fontWeight: 600 }}>{ans.likes || 0}</span>
                                                                </div>
                                                                <div style={{ display: "flex", alignItems: "center", gap: 4, color: '#faad14', fontSize: '0.8rem' }} title="Neutrals">
                                                                    <MehFilled style={{ fontSize: '14px' }} /> <span style={{ fontWeight: 600 }}>{ans.neutral || 0}</span>
                                                                </div>
                                                                <div style={{ display: "flex", alignItems: "center", gap: 4, color: '#ff4d4f', fontSize: '0.8rem' }} title="Dislikes">
                                                                    <DislikeFilled style={{ fontSize: '14px' }} /> <span style={{ fontWeight: 600 }}>{ans.dislikes || 0}</span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div style={{ display: "flex", gap: 4 }}>
                                                                <Button
                                                                    size="small"
                                                                    type="text"
                                                                    style={{
                                                                        color: liked ? '#52c41a' : (isMe ? 'rgba(255,255,255,0.5)' : '#ccc'),
                                                                        height: 24, width: 24, padding: 0
                                                                    }}
                                                                    icon={liked ? <LikeFilled /> : <LikeOutlined />}
                                                                    onClick={() => rate("Like", ans.id, record?.id_aula)}
                                                                />
                                                                <Button
                                                                    size="small"
                                                                    type="text"
                                                                    style={{
                                                                        color: neutral ? '#faad14' : (isMe ? 'rgba(255,255,255,0.5)' : '#ccc'),
                                                                        height: 24, width: 24, padding: 0
                                                                    }}
                                                                    icon={neutral ? <MehFilled /> : <MehOutlined />}
                                                                    onClick={() => rate("Neutral", ans.id, record?.id_aula)}
                                                                />
                                                                <Button
                                                                    size="small"
                                                                    type="text"
                                                                    style={{
                                                                        color: disliked ? '#ff4d4f' : (isMe ? 'rgba(255,255,255,0.5)' : '#ccc'),
                                                                        height: 24, width: 24, padding: 0
                                                                    }}
                                                                    icon={disliked ? <DislikeFilled /> : <DislikeOutlined />}
                                                                    onClick={() => rate("Dislike", ans.id, record?.id_aula)}
                                                                />
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    {((record && record.id === 0) || (record && record.estat === 0)) && (
                        <div style={{
                            padding: '20px',
                            backgroundColor: '#fff',
                            borderTop: '1px solid #e5e5e5',
                            boxShadow: '0 -2px 10px rgba(0,0,0,0.02)'
                        }}>
                            <div style={{
                                backgroundColor: '#fffbe6',
                                border: '1px solid #ffe58f',
                                padding: '8px 12px',
                                marginBottom: '12px',
                                borderRadius: '4px',
                                color: '#d48806',
                                fontSize: '0.85rem'
                            }}>
                                <strong>Atención:</strong> Cuando termines un chat con la IA recuerda salir de la cola con la flecha hacia abajo especificando el motivo por el que cierras el chat.
                            </div>
                            <Form form={form} onFinish={handleSend}>
                                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                                    <div style={{ flex: 1, position: 'relative' }}>
                                        <Form.Item
                                            name="message"
                                            style={{ marginBottom: 0 }}
                                            rules={[
                                                { required: true, message: "" },
                                                { min: 5, message: "Mínim 5 caràcters" }
                                            ]}
                                        >
                                            <Input.TextArea
                                                onPaste={(e) => copypaste === 0 && e.preventDefault()}
                                                onCopy={(e) => copypaste === 0 && e.preventDefault()}
                                                placeholder="Escriu el teu missatge aquí..."
                                                autoSize={{ minRows: 1, maxRows: 6 }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        handleSend();
                                                    }
                                                }}
                                                style={{
                                                    borderRadius: '24px',
                                                    resize: 'none',
                                                    padding: '12px 20px',
                                                    paddingRight: '40px',
                                                    border: '1px solid #ddd',
                                                    backgroundColor: '#f9f9f9',
                                                    fontSize: '0.95rem'
                                                }}
                                            />
                                        </Form.Item>
                                    </div>

                                    <Button
                                        type="primary"
                                        shape="circle"
                                        onClick={handleSend}
                                        size="large"
                                        loading={sending}
                                        style={{
                                            flexShrink: 0,
                                            backgroundColor: '#10a37f',
                                            width: 46, height: 46,
                                            boxShadow: '0 4px 12px rgba(16, 163, 127, 0.3)'
                                        }}
                                        icon={<SendOutlined style={{ fontSize: 18, marginLeft: 2 }} />}
                                    />

                                    {record?.id !== 0 && userId == record?.id_user && (
                                        <Dropdown
                                            menu={{ items: itemsIA, onClick: handleMenuClick }}
                                            placement="topRight"
                                            trigger={['click']}
                                        >
                                            <Button
                                                type="default"
                                                shape="circle"
                                                size="large"
                                                style={{ width: 46, height: 46, borderColor: '#ddd' }}
                                                icon={<DownOutlined />}
                                            />
                                        </Dropdown>
                                    )}
                                </div>
                            </Form>
                            <div style={{ textAlign: 'center', fontSize: '0.7em', color: '#999', marginTop: 10 }}>
                                Lanterns AI pot cometre errors. Verifica la informació important.
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    return (
        <>
            {inline ? (
                innerContent
            ) : (
                <Modal
                    open={modalVisible}
                    onCancel={() => {
                        setModalVisible(false);
                        if (onClose) onClose();
                    }}
                    footer={null}
                    width={900}
                    centered
                    className="chat-modal-modern"
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 30 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <Avatar size={40} src="/images/aiavatar1.png" icon={<RobotOutlined />} style={{ backgroundColor: '#10a37f' }} />
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontSize: '1.1em', fontWeight: 700, color: '#333' }}>Asistente Lanterns</span>
                                    <span style={{ fontSize: '0.8em', color: '#888' }}>{record?.id ? `ID: ${record.id}` : 'Nueva Conversación'}</span>
                                </div>
                            </div>
                            {canSeeCode && (
                                <Button icon={<CodeOutlined />} onClick={() => setStatsVisible(true)} type="default" style={{ marginRight: 30 }}>
                                    Veure Codi Enviat
                                </Button>
                            )}
                            {fromWelcome && dudasEnabled && user.tipus === "2" && !isEnCua && (
                                <Button
                                    type="primary"
                                    danger
                                    icon={<UserOutlined />}
                                    onClick={async () => {
                                        setSending(true);
                                        await PostDuda(record?.id_aula || "-1", userId, "-1", 0, 0, 0);
                                        setSending(false);
                                        if (onProfRequest) onProfRequest();
                                        setModalVisible(false);
                                        if (onClose) onClose();
                                    }}
                                    style={{ marginRight: 30 }}
                                >
                                    Demanar ajuda al Professor/a
                                </Button>
                            )}
                        </div>
                    }
                    bodyStyle={{ padding: 0 }}
                >
                    {innerContent}
                </Modal>
            )}

            {/* Statistics Modal */}
            <Modal
                title={`Estadístiques de l'usuari ${record?.nomAlumne || 'desconegut'}`}
                open={statsVisible}
                onCancel={() => setStatsVisible(false)}
                footer={null}
                width={1000}
                centered
                className="stats-modal"
            >
                {record?.id_user && (
                    <ProblemaIdEstadistiques
                        userId={record.id_user + ""}
                        idProblem={(record.id_problema || 0) + ""}
                        idActivity={(record.id_practica || 0) + ""}
                    />
                )}
            </Modal>
        </>
    );
};

export default ChatModal;
